import React from "react";
import { ColumnDef, flexRender } from "@tanstack/react-table";
import { useNavigation } from "@refinedev/core";
import { GetFieldsFromList } from "@refinedev/hasura";
import { useTable } from "@refinedev/react-table";

// import { USERS_QUERY } from "./queries";
import { GetUsersQuery } from "graphql/types";
import dayjs from "dayjs";
import { FaChevronUp, FaChevronDown } from "react-icons/fa6";
import { useProgressbar } from "@utils/notifications";
import { DefaultTemplate } from "@components/templates/default";
import SearchBar from "components/SearchBar";
import { Auth_Users } from "@graphql/schema.types";

export const UserList: React.FC = () => {
  const { edit, create } = useNavigation();

  const columns = React.useMemo<ColumnDef<Auth_Users>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
      },
      {
        id: "name",
        header: "Name",
        accessorKey: "display_name",
        meta: {
          filterOperator: "contains",
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => {
          return <div className="badge badge-neutral m-0.5">
            {getValue()}
          </div>
        }
      },
      {
        id: 'pro_name',
        header: 'Professional',
        accessorFn: row => row.appointments[0]?.professional?.name ?? '-'
      },
      {
        id: "created_at",
        header: "CreatedAt",
        accessorKey: "created_at",
        cell: function render({ getValue }) {
          return dayjs(getValue()).format('DD/MM/YYYY, hh:mm:ss a')
        },
      },
      {
        id: "action",
        Header: "Action",
        accessorKey: "id",
        cell: ({ getValue }) => {
          return (
            <button className="btn btn-primary btn-sm" onClick={() => edit("user", getValue() as number)}>
              Edit
            </button>
          );
        },
      },
    ],
    [],
  );

  const {
    getHeaderGroups,
    getRowModel,
    getState,
    setPageIndex,
    getCanPreviousPage,
    getPageCount,
    getCanNextPage,
    nextPage,
    previousPage,
    setPageSize,
    getColumn,
    refineCore: { tableQueryResult }
    // filters, sorters
  } = useTable
      <GetFieldsFromList<GetUsersQuery>>
      ({
        columns,
        refineCoreProps: {
          resource: 'auth_users',
          meta: {
            fields: ['id', 'display_name', 'status', 'created_at', {
              appointments: ['id', 'professional_id', 'datetime_start', 'datetime_end',
                {
                  professional: ['id', 'display_name', 'default_role']
                }
              ]
            }],
          },
        }
      });

  useProgressbar(tableQueryResult.isLoading, 5)

  const nameColumn = getColumn("name");

  return (
    <DefaultTemplate
      subnavbar={
        <SearchBar
          value={(nameColumn?.getFilterValue() as string) ?? ""}
          onChange={(event) => nameColumn?.setFilterValue(event.target.value)}
          onClear={(event) => nameColumn?.setFilterValue('')}
        />
      }
    >
      <div className="card md:w-1/2 bg-base-100 m-4">
        <div className="card-body">
          <h2 className="card-title">Usuarios</h2>
          <button className="btn btn-primary" onClick={() => create("posts")}>Create User</button>
        </div>
      </div>
      <div className="card bg-base-100 m-4">
        <div className="block scrollbar-thin overflow-x-auto">
          <table className="table">
            <thead>
              {getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center" onClick={header.column.getToggleSortingHandler()}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: <FaChevronUp />,
                            desc: <FaChevronDown />
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {getRowModel().rows.map((row) => {
                return (
                  <tr className="hover" key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="card-actions overflow-visible py-2 px-4">
          <div className="flex scrollbar-thin overflow-x-auto whitespace-nowrap items-center justify-start overflow-y-visible">
            <div className="mr-4">
              Page
              <strong>
                {" "}{getState().pagination.pageIndex + 1} of {getPageCount()}
              </strong>
            </div>
            <div className="join stepper">
              <button className="join-item btn btn-sm" disabled={!getCanPreviousPage()} onClick={() => previousPage()}>-</button> {/* setPageIndex(0) */}
              <input className="join-item input input-bordered w-10 text-center input-sm" onChange={(e) => setPageIndex(e.target.value ? Number(e.target.value) - 1 : 0)}
                value={getState().pagination.pageIndex + 1} />
              <button className="join-item btn btn-sm" disabled={!getCanNextPage()} onClick={() => nextPage()}>+</button> {/*  setPageIndex(getPageCount() - 1) */}
            </div>
            <div className="ml-4">
              <span className="m-1">Show </span>
              <div className="join">
                {[10, 20, 30, 40, 50].map((pageSize, i) => (
                  <button
                    key={i}
                    className={`font-bold btn btn-xs btn-outline join-item ${pageSize === getState().pagination.pageSize ? 'btn-active' : ''}`}
                    onClick={(e) => {
                      setPageSize(pageSize)
                    }}
                  >{pageSize}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DefaultTemplate >
  );
};
